// frontend/src/components/BudgetEditor.tsx
import React, { useState, useEffect, useCallback, useRef, useMemo, useTransition } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import "./BudgetEditor.css";
import "./AdminPanel.css";

const months = [
  "April", "May", "June", "July", "August", "September",
  "October", "November", "December", "January", "February", "March",
];

const monthAbbr: Record<string, string> = {
  April: "Apr", May: "May", June: "Jun", July: "Jul", August: "Aug",
  September: "Sep", October: "Oct", November: "Nov", December: "Dec",
  January: "Jan", February: "Feb", March: "Mar",
};

const dimensionStartMonth: Record<string, number> = {
  "July Forecast": 3,
  "October Forecast": 6,
  "January Forecast": 9,
};

// Reference dimension used to compute variance for each working dimension
const dimensionReference: Record<string, string> = {
  "Annual Budget":    "Annual Budget",
  "Actuals":          "Actuals",
  "July Forecast":    "Annual Budget",
  "October Forecast": "July Forecast",
  "January Forecast": "October Forecast",
};

const CATEGORY_ORDER: Record<string, number> = {
  COMPENSATION: 1,
  OCCUPANCY: 2,
  OPERATING: 3,
  ADVERTISING: 4,
  ALLOCATED: 5,
  AMORT: 6,
  CORP_OH: 7,
  OTHERADJUSTMENTS: 8,
};
const getCategoryRank = (cat: string): number =>
  CATEGORY_ORDER[cat.trim().toUpperCase()] ?? 99;

// Extract leading digits from account string for numeric sort
const parseAccountNum = (s: string): number => {
  const m = s.match(/^\d+/);
  return m ? parseInt(m[0], 10) : 0;
};

const CATEGORY_DISPLAY: Record<string, string> = {
  COMPENSATION: "Compensation", OCCUPANCY: "Occupancy", OPERATING: "Operating",
  ADVERTISING: "Advertising", ALLOCATED: "Allocations",
  AMORT: "Amortization", CORP_OH: "Corp O/H", OTHERADJUSTMENTS: "Other",
};

const CATEGORY_COLORS: Record<string, string> = {
  COMPENSATION: "#4A90E2",
  OCCUPANCY:    "#e84393",
  OPERATING:    "#ea580c",
  ADVERTISING:  "#7c3aed",
  ALLOCATED:    "#475569",
  AMORT:        "#0891b2",
  CORP_OH:      "#059669",
  OTHERADJUSTMENTS: "#64748b",
};

// Auto-calculated salary accounts — read-only, sourced from SalaryView/BIPView
const LOCKED_ACCOUNTS = new Set(["60010", "60022", "60100", "60120", "60130"]);
const LOCKED_ACCOUNT_LABELS: Record<string, string> = {
  "60010": "Salaries-Employees",
  "60022": "Incentive Bonus (BIP)",
  "60100": "Insurance Benefits",
  "60120": "Payroll Tax",
  "60130": "Retirement Plans",
};
// Renders account code + name on separate lines, protecting the code from word-break
const AccountCell = React.memo(function AccountCell({ account }: { account: string }) {
  const dash = account.indexOf(" - ");
  if (dash === -1) return <span style={{ fontWeight: 600, fontSize: 11 }}>{account}</span>;
  const code = account.slice(0, dash);
  const name = account.slice(dash + 3);
  return (
    <>
      <div style={{ fontWeight: 700, fontSize: 11, whiteSpace: "nowrap" }}>{code}</div>
      <div style={{ fontSize: 10, color: "#6b7280", lineHeight: 1.3 }}>{name}</div>
    </>
  );
});

// Returns the short numeric key if this account is a locked account (prefix match),
// e.g. "60010 - Salaries-Employees" → "60010". Returns null if not locked.
const getLockedKey = (account: string): string | null => {
  const num = account.match(/^\d+/)?.[0] || "";
  return LOCKED_ACCOUNTS.has(num) ? num : null;
};

// Cached formatter — avoids creating a new Intl.NumberFormat on every call
const _currencyFmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 });
const currencyFmt = (value: number) => _currencyFmt.format(value);

interface BudgetItem {
  account: string;
  month: string;
  budget: number;
  actual: number;
  category?: string;
}

interface BudgetEditorProps {
  costCenterNumber: string;
  costCenterName?: string;
  costCenterOwner?: string;
  fiscalYear?: string;
  isAdmin?: boolean;
}

interface DownloadSummary {
  filename: string;
  dimension: string;
  categoryTotals: { cat: string; label: string; total: number }[];
  grandTotal: number;
}

const BudgetEditor: React.FC<BudgetEditorProps> = ({ costCenterNumber, costCenterName, costCenterOwner, fiscalYear, isAdmin }) => {
  const [, startTransition] = useTransition();
  const [dimension, setDimension] = useState<string>("July Forecast");
  const [currentDimension, setCurrentDimension] = useState("Annual Budget");
  const [forecastActive, setForecastActive] = useState(false);
  const [editOverride, setEditOverride] = useState(false);
  const [salaryAutoCompute, setSalaryAutoCompute] = useState(true);
  const [forecastDim, setForecastDim] = useState<string | null>(null);
  const [fcComments, setFcComments] = useState<Record<string, string>>({});
  const [fcCommentDraft, setFcCommentDraft] = useState("");
  const [fcCommentSaving, setFcCommentSaving] = useState(false);
  const [fcCommentSaved, setFcCommentSaved] = useState(false);
  const [fcCommentOpen, setFcCommentOpen] = useState(false);
  const [fcCommentDim, setFcCommentDim] = useState("");
  const [dimCloseDate, setDimCloseDate] = useState<string | null>(null);
  const [successModal, setSuccessModal] = useState<{ dim: string; total: number; variance: number; closeDate: string | null } | null>(null);
  const [draftSavedModal, setDraftSavedModal] = useState<{ count: number; dim: string } | null>(null);
  const [budgetData, setBudgetData] = useState<Record<string, BudgetItem[]>>({});
  const [dataVersion, setDataVersion] = useState(0);
  const [annualBudgetData, setAnnualBudgetData] = useState<Record<string, BudgetItem[]>>({});
  const [subtotals, setSubtotals] = useState<Record<string, number>>({});
  const [updatedData, setUpdatedData] = useState<BudgetItem[]>([]);
  const [showVarAlert, setShowVarAlert] = useState(false);
  const [showTargetAlert, setShowTargetAlert] = useState(false);
  const [ccTarget, setCcTarget] = useState(0);
  const [varDimsForCC, setVarDimsForCC] = useState<Record<string, number>>({});
  const varianceAllowed = varDimsForCC[dimension] ?? 0;
  const [, setBudgetConsumedPercentage] = useState<number>(0);
  const [overallAnnualBudgetTotal, setOverallAnnualBudgetTotal] = useState<number>(0);
  const [overallActualsTotal, setOverallActualsTotal] = useState<number>(0);
  const [categoryFilter, setCategoryFilter] = useState<string>("All");
  const [accountSearch, setAccountSearch] = useState<string>("");
  const [referenceData, setReferenceData] = useState<Record<string, BudgetItem[]>>({});
  const [mpyReferenceData, setMpyReferenceData] = useState<Record<string, BudgetItem[]>>({});
  const [actualsFullData, setActualsFullData] = useState<Record<string, BudgetItem[]>>({});
  const [lockedData, setLockedData] = useState<Record<string, Record<string, number>>>({});
  const [actualsMonths, setActualsMonths] = useState<string[]>([]);
  const [excelMode, setExcelMode] = useState(false);
  const [simplifiedView, setSimplifiedView] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadSummary, setDownloadSummary] = useState<DownloadSummary | null>(null);

  // Export modal state
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportFile, setExportFile] = useState<File | null>(null);
  const [exportPassword, setExportPassword] = useState("");
  const [exportNeedsPassword, setExportNeedsPassword] = useState(false);
  const [exportDimension, setExportDimension] = useState<string>("Annual Budget");
  const [exportValidating, setExportValidating] = useState(false);
  const [exportValidation, setExportValidation] = useState<{
    valid: boolean; ccMatch: boolean; fileCC: string | null;
    dimension: string; filename: string; fy: string;
    sheets: { salary: boolean; bip: boolean; plan: boolean; planSheetName: string | null; planSheets?: string[] };
    writeTargets?: { sheet: string; description: string }[];
    planMonthStartCol?: number;
    allSheets: string[]; counts: { employees: number; budgetAccounts: number };
  } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportPlanSheet, setExportPlanSheet] = useState<string>("");
  const [accountColWidth, setAccountColWidth] = useState(120);
  const accountColRef = useRef<HTMLTableCellElement>(null);
  const isResizingRef = useRef(false);

  const handleMouseDown = () => { isResizingRef.current = true; };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isResizingRef.current && accountColRef.current) {
      const newWidth = e.clientX - accountColRef.current.getBoundingClientRect().left;
      if (newWidth >= 80 && newWidth <= 400) setAccountColWidth(newWidth);
    }
  }, []);

  const handleMouseUp = () => { isResizingRef.current = false; };

  useEffect(() => {
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseMove]);

  // Fetch forecast config — enables/disables pills and highlights the active dim
  useEffect(() => {
    api.get("/settings/forecast-config")
      .then(res => {
        const active = res.data.active as boolean;
        const dim = (res.data.dimension || null) as string | null;
        setForecastActive(active);
        setForecastDim(dim);
        setDimCloseDate(res.data.close_date ? new Date(res.data.close_date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null);
        setSalaryAutoCompute(res.data.salary_auto_compute !== false);
        if (dim) {
          setDimension(dim);
          api.get(`/settings/cc-edit-override/${costCenterNumber}`, { params: { dimension: dim } })
            .then(r => setEditOverride(r.data.enabled === true))
            .catch(() => {});
          const fcDims = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];
          Promise.all(fcDims.map(d =>
            api.get(`/settings/forecast-comment/${costCenterNumber}`, { params: { dimension: d } })
              .then(r => [d, r.data.comment || ""] as [string, string])
              .catch(() => [d, ""] as [string, string])
          )).then(pairs => {
            const m: Record<string, string> = {};
            pairs.forEach(([d, c]) => { m[d] = c; });
            setFcComments(m);
            setFcCommentDim(dim);
            setFcCommentDraft(m[dim] || "");
          });
        }
      })
      .catch(() => {});
    api.get(`/settings/forecast-variance-for-cc/${costCenterNumber}`)
      .then(res => { setVarDimsForCC(res.data.dims ?? {}); })
      .catch(() => {});
    api.get(`/admin/cc-target/${costCenterNumber}`)
      .then(res => setCcTarget(res.data.target_amount ?? 0))
      .catch(() => {});
  }, []);

  const isDimOpen = (dim: string): boolean => {
    if (isAdmin) return true;
    if (editOverride && dim === forecastDim) return true;
    if (!forecastActive) return false;
    if (dim === "Annual Budget") return forecastDim === "Annual Budget";
    return dim === forecastDim;
  };

  const dimWindowTitle = (_dim: string): string =>
    forecastActive ? "Not the active forecast cycle" : "Forecast planning is currently disabled";

  const isAcctLocked = useCallback((account: string): boolean => {
    if (!salaryAutoCompute) return false;
    return getLockedKey(account) !== null;
  }, [salaryAutoCompute]);

  const calculateSubtotals = useCallback((data: Record<string, BudgetItem[]>, locked: Record<string, Record<string, number>> = {}) => {
    const categoryTotals: Record<string, number> = {};
    Object.entries(data).forEach(([acc, entries]) => {
      if (salaryAutoCompute && getLockedKey(acc)) return; // skip — counted separately via lockedData below
      entries.forEach(({ category, budget }) => {
        const key = category?.trim().toUpperCase() || "OTHERADJUSTMENTS";
        categoryTotals[key] = (categoryTotals[key] || 0) + budget;
      });
    });
    // Locked salary accounts all belong to COMPENSATION (only when auto-compute is on)
    if (salaryAutoCompute) {
      Object.entries(locked).forEach(([, monthVals]) => {
        const total = Object.values(monthVals).reduce((s, v) => s + v, 0);
        categoryTotals["COMPENSATION"] = (categoryTotals["COMPENSATION"] || 0) + total;
      });
    }
    setSubtotals(categoryTotals);
  }, [salaryAutoCompute]);

  // Keep subtotals pill strip in sync whenever budgetData or lockedData changes
  useEffect(() => { calculateSubtotals(budgetData, lockedData); }, [budgetData, lockedData, calculateSubtotals]);

  useEffect(() => {
    api.get("/settings/current-dimension")
      .then((res) => { setCurrentDimension(res.data.dimension); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (costCenterNumber && dimension) {
      const fyParam = fiscalYear ? `&fy=${fiscalYear}` : "";
      api.get(`/budgets?cost_center=${costCenterNumber}&dimension=${dimension}${fyParam}`)
        .then((response) => { setBudgetData(response.data); calculateSubtotals(response.data); })
        .catch((error) => console.error("❌ Error fetching budget data:", error));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [costCenterNumber, dimension, calculateSubtotals, fiscalYear, dataVersion]);

  // Fetch salary-computed data for locked account rows
  useEffect(() => {
    if (!costCenterNumber) return;
    api.get(`/salary/computed?cost_center=${costCenterNumber}`)
      .then(res => {
        const map: Record<string, Record<string, number>> = {};
        (res.data as { account: string; months: Record<string, number> }[]).forEach(row => {
          map[row.account] = row.months;
        });
        setLockedData(map);
      })
      .catch(() => {}); // Silent fail — locked rows just show "–"
  }, [costCenterNumber]);

  useEffect(() => {
    if (costCenterNumber) {
      const fyParam = fiscalYear ? `&fy=${fiscalYear}` : "";
      api.get(`/budgets?cost_center=${costCenterNumber}&dimension=Annual Budget${fyParam}`)
        .then((response) => {
          const annualData: Record<string, BudgetItem[]> = response.data;
          setAnnualBudgetData(annualData);
          let sum = 0;
          Object.values(annualData).forEach((entries) => entries.forEach((item) => { sum += item.budget || 0; }));
          setOverallAnnualBudgetTotal(sum);
        })
        .catch((error) => console.error("❌ Error fetching Annual Budget data:", error));
    }
  }, [costCenterNumber, fiscalYear]);

  // Fetch the reference dimension data used for variance calculation
  useEffect(() => {
    if (!costCenterNumber || !dimension) return;
    const refDim = dimensionReference[dimension] ?? "Annual Budget";
    const fyParam = fiscalYear ? `&fy=${fiscalYear}` : "";
    api.get(`/budgets?cost_center=${costCenterNumber}&dimension=${refDim}${fyParam}`)
      .then((res) => setReferenceData(res.data))
      .catch((err) => console.error("Error fetching reference data:", err));
  }, [costCenterNumber, dimension, fiscalYear]);

  // Fetch FY26 MPY (January Forecast) as reference when editing FY27 Annual Budget
  useEffect(() => {
    if (!costCenterNumber || !fiscalYear) return;
    const fyNum = parseInt(fiscalYear.replace(/\D/g, ""), 10) || 0;
    if (fyNum < 27 || dimension !== "Annual Budget") { setMpyReferenceData({}); return; }
    const prevFY = `FY${fyNum - 1}`;
    api.get(`/budgets?cost_center=${costCenterNumber}&dimension=January Forecast&fy=${prevFY}`)
      .then(res => setMpyReferenceData(res.data))
      .catch(() => setMpyReferenceData({}));
  }, [costCenterNumber, fiscalYear, dimension]);

  useEffect(() => {
    if (costCenterNumber) {
      const fyParam = fiscalYear ? `&fy=${fiscalYear}` : "";
      api.get(`/budgets?cost_center=${costCenterNumber}&dimension=Actuals${fyParam}`)
        .then((response) => {
          const data: Record<string, BudgetItem[]> = response.data;
          setActualsFullData(data);
          let sum = 0;
          Object.values(data).forEach((entries) => entries.forEach((item) => { sum += item.budget || 0; }));
          setOverallActualsTotal(sum);
        })
        .catch((error) => console.error("❌ Error fetching Actuals data:", error));
    }
  }, [costCenterNumber, fiscalYear]);

  // Fetch which months have actuals uploaded (for blending actuals into locked rows)
  useEffect(() => {
    if (costCenterNumber) {
      const fy = fiscalYear || "FY26";
      api.get(`/budgets/months-with-actuals?cost_center=${costCenterNumber}&fy=${fy}`)
        .then(res => setActualsMonths(res.data.months || []))
        .catch(() => {});
    }
  }, [costCenterNumber, fiscalYear]);

  useEffect(() => {
    setBudgetConsumedPercentage(
      overallAnnualBudgetTotal > 0 ? (overallActualsTotal / overallAnnualBudgetTotal) * 100 : 0
    );
  }, [overallAnnualBudgetTotal, overallActualsTotal]);

  const handleChange = (account: string, month: string, value: number) => {
    // Mark the KPI/subtotal recalculation as low-priority so the input stays responsive
    startTransition(() => {
      setBudgetData((prevData) => ({
        ...prevData,
        [account]: prevData[account].map((entry) =>
          entry.month === month ? { ...entry, budget: value } : entry
        ),
      }));
    });

    setUpdatedData((prev) => {
      const original = budgetData[account]?.find((e) => e.month === month);
      const updatedEntry: BudgetItem = {
        account, month, budget: value,
        actual: original?.actual || 0,
        category: original?.category || "Uncategorized",
      };
      const existing = prev.find((e) => e.account === account && e.month === month);
      return existing
        ? prev.map((e) => e.account === account && e.month === month ? updatedEntry : e)
        : [...prev, updatedEntry];
    });

    // Unsaved edits → revert status to draft
    if (submissionStatus === "submitted") {
      setSubmissionStatus("draft");
    }
  };

  const [submissionStatus, setSubmissionStatus] = useState<string | null>(null);

  // Fetch current submission status for this CC + dimension
  useEffect(() => {
    if (!costCenterNumber || !dimension) return;
    const fyParam = fiscalYear ? `&fy=${fiscalYear}` : "";
    api.get(`/budgets/submission-status?cost_center=${costCenterNumber}&dimension=${dimension}${fyParam}`)
      .then(res => setSubmissionStatus(res.data.status || null))
      .catch(() => setSubmissionStatus(null));
  }, [costCenterNumber, dimension, fiscalYear]);

  const handleSaveDraft = () => {
    if (updatedData.length === 0) return;

    const sanitized = updatedData.map(({ account, month, budget, category }) => ({
      cost_center: costCenterNumber, dimension, account, month, budget,
      category: category || "Uncategorized",
    }));

    // Optimistic: show modal immediately, persist in background
    const count = sanitized.length;
    setUpdatedData([]);
    setSubmissionStatus("draft");
    setDraftSavedModal({ count, dim: dimension });

    api.post("/budgets/update", { updatedData: sanitized, fiscal_year: fiscalYear, status: "draft" })
      .then(() => { setDataVersion(v => v + 1); })
      .catch((error) => {
        console.error("Error saving draft:", error.response?.data || error.message);
        setDraftSavedModal(null);
        alert("Error saving draft. Please try again or contact your administrator.");
      });
  };

  const handleSubmit = () => {
    const hasChanges = updatedData.length > 0;
    const isDraftResubmit = !hasChanges && submissionStatus === "draft";

    if (!hasChanges && !isDraftResubmit) {
      alert("No changes to submit. Edit a cell first.");
      return;
    }

    // Block submit if controllable forecast exceeds target (excludes CORP_OH, AMORT, OTHERADJUSTMENTS)
    const effectiveTarget = ccTarget > 0 ? ccTarget : (kpiRefTotal + varianceAllowed);
    if (dimension !== "Annual Budget" && effectiveTarget > 0 && Math.round(kpiDimTotal) > Math.round(effectiveTarget)) {
      if (ccTarget > 0) {
        setShowTargetAlert(true);
      } else {
        setShowVarAlert(true);
      }
      return;
    }

    const sanitized = updatedData.map(({ account, month, budget, category }) => ({
      cost_center: costCenterNumber, dimension, account, month, budget,
      category: category || "Uncategorized",
    }));

    // Optimistic: show modal immediately, persist in background
    setUpdatedData([]);
    setSubmissionStatus("submitted");
    setSuccessModal({
      dim:       dimension,
      total:     dimTotal,
      variance:  overallAnnualBudgetTotal - dimTotal,
      closeDate: dimCloseDate,
    });

    if (hasChanges) {
      // Submit with budget changes
      api.post("/budgets/update", { updatedData: sanitized, fiscal_year: fiscalYear, status: "submitted" })
        .then(() => { setDataVersion(v => v + 1); })
        .catch((error) => {
          console.error("Error updating budget:", error.response?.data || error.message);
          setSuccessModal(null);
          setSubmissionStatus("draft");
          alert("Error saving budget. Please try again or contact your administrator.");
        });
    } else {
      // Draft re-submit: just update the submission status, no budget changes
      api.post("/budgets/update", { updatedData: [], cost_center: costCenterNumber, dimension, fiscal_year: fiscalYear, status: "submitted" })
        .then(() => { setDataVersion(v => v + 1); })
        .catch((error) => {
          console.error("Error updating submission status:", error.response?.data || error.message);
          setSuccessModal(null);
          setSubmissionStatus("draft");
          alert("Error submitting. Please try again or contact your administrator.");
        });
    }
  };

  const formatCurrency = currencyFmt;
  const formatCurrencyFull = currencyFmt;

  const isEditableMonth = (dim: string, month: string): boolean =>
    dimensionStartMonth[dim] !== undefined && months.indexOf(month) >= dimensionStartMonth[dim];

  // Grid navigation: Tab → next cell right (wraps to next row), Enter → cell below
  const handleCellKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Tab" && e.key !== "Enter") return;
    e.preventDefault();
    const table = (e.target as HTMLElement).closest("table");
    if (!table) return;
    const editableCells = Array.from(
      table.querySelectorAll<HTMLInputElement>("input.editable-cell--active:not(:disabled)")
    );
    const idx = editableCells.indexOf(e.target as HTMLInputElement);
    if (idx === -1) return;

    if (e.key === "Tab") {
      // Tab (or Shift+Tab): move right/left, wrap rows
      const next = e.shiftKey ? idx - 1 : idx + 1;
      if (next >= 0 && next < editableCells.length) {
        editableCells[next].focus();
        editableCells[next].select();
      }
    } else if (e.key === "Enter") {
      // Enter: move to the same column in the next row
      const row = (e.target as HTMLElement).closest("tr");
      if (!row) return;
      const cellsInRow = Array.from(
        row.querySelectorAll<HTMLInputElement>("input.editable-cell--active:not(:disabled)")
      );
      const colIdx = cellsInRow.indexOf(e.target as HTMLInputElement);
      // Find next editable row
      let nextRow = row.nextElementSibling;
      while (nextRow && !nextRow.querySelector("input.editable-cell--active:not(:disabled)")) {
        nextRow = nextRow.nextElementSibling;
      }
      if (nextRow) {
        const nextRowCells = Array.from(
          nextRow.querySelectorAll<HTMLInputElement>("input.editable-cell--active:not(:disabled)")
        );
        const target = nextRowCells[colIdx] || nextRowCells[0];
        if (target) { target.focus(); target.select(); }
      }
    }
  };

  const formatVarianceNumber = (variance: number): string => {
    if (variance < 0) return `(${Math.abs(variance).toFixed(0)})`;
    return variance.toFixed(0);
  };

  const getAccountCategory = useCallback((account: string): string => {
    if (salaryAutoCompute && LOCKED_ACCOUNTS.has(account)) return "COMPENSATION";
    return (annualBudgetData[account]?.[0]?.category || budgetData[account]?.[0]?.category || "").trim().toUpperCase() || "OTHERADJUSTMENTS";
  }, [annualBudgetData, budgetData, salaryAutoCompute]);

  const allAccounts = useMemo(() => Array.from(new Set([
    ...Object.keys(budgetData).filter(acc => !(salaryAutoCompute && getLockedKey(acc))),
    ...Object.keys(annualBudgetData).filter(acc => !(salaryAutoCompute && getLockedKey(acc))),
    ...(salaryAutoCompute ? Object.keys(lockedData) : []),
  ])).sort((a, b) => {
    const catA = getCategoryRank(getAccountCategory(a));
    const catB = getCategoryRank(getAccountCategory(b));
    if (catA !== catB) return catA - catB;
    return parseAccountNum(a) - parseAccountNum(b);
  }), [budgetData, annualBudgetData, lockedData, getAccountCategory, salaryAutoCompute]);

  const uniqueCategories = useMemo(() => Array.from(new Set(allAccounts.map(getAccountCategory)))
    .filter(cat => CATEGORY_ORDER[cat] !== undefined)
    .sort((a, b) => getCategoryRank(a) - getCategoryRank(b)),
  [allAccounts, getAccountCategory]);

  const visibleAccounts = useMemo(() => allAccounts
    .filter(acc => categoryFilter === "All" || getAccountCategory(acc) === categoryFilter)
    .filter(acc => !accountSearch || acc.toLowerCase().includes(accountSearch.toLowerCase()))
    .filter(acc => {
      if (!simplifiedView) return true;
      const sum = (data: Record<string, BudgetItem[]>) =>
        (data[acc] || []).reduce((s, e) => s + (e.budget || 0), 0);
      if (Math.round(sum(budgetData)) !== 0) return true;
      if (Math.round(sum(referenceData)) !== 0) return true;
      if (Math.round(sum(annualBudgetData)) !== 0) return true;
      if (Math.round(sum(actualsFullData)) !== 0) return true;
      return false;
    }),
  [allAccounts, categoryFilter, accountSearch, getAccountCategory, simplifiedView, budgetData, referenceData, annualBudgetData, actualsFullData]);

  // KPI totals scoped to whichever accounts are visible (respects category filter)
  // For locked accounts, current-dimension values come from lockedData (computed),
  // but reference/annual totals use stored DB values for consistency with admin views.
  const lockedMonthTotal = useCallback((acc: string) =>
    salaryAutoCompute && LOCKED_ACCOUNTS.has(acc) ? Object.values(lockedData[acc] || {}).reduce((s, v) => s + v, 0) : 0,
  [lockedData, salaryAutoCompute]);

  // Look up stored budget total for a locked account number from a budget data record.
  // Maps account number (e.g. "60010") to the full key (e.g. "60010 - Salaries-Employees").
  const storedLockedTotal = useCallback((accNum: string, data: Record<string, BudgetItem[]>) => {
    const fullKey = Object.keys(data).find(k => getLockedKey(k) === accNum);
    if (!fullKey) return lockedMonthTotal(accNum); // fallback to computed
    return (data[fullKey] || []).reduce((s, e) => s + (e.budget || 0), 0);
  }, [lockedMonthTotal]);

  // Per-month value for locked accounts: returns ACTUALS for months with uploaded actuals,
  // COMPUTED values for future months. This blends real spending with salary engine estimates.
  const lockedMonthValue = useCallback((acc: string, month: string): number => {
    if (actualsMonths.includes(month)) {
      // Use actual value from the Actuals dimension (not the current forecast dimension)
      const fullKey = Object.keys(actualsFullData).find(k => getLockedKey(k) === acc);
      if (fullKey) {
        const entry = (actualsFullData[fullKey] || []).find(e => e.month === month);
        if (entry) return entry.budget || 0;
      }
    }
    // Otherwise use computed value from salary engine
    return lockedData[acc]?.[month] || 0;
  }, [actualsMonths, actualsFullData, lockedData]);

  // Annual total for locked accounts using blended actuals+computed values
  const lockedAnnualTotal = useCallback((acc: string): number =>
    months.reduce((sum, m) => sum + lockedMonthValue(acc, m), 0),
  [lockedMonthValue]);

  // Annual Budget total — uses STORED values for locked accounts (matches admin Forecast Status)
  const filteredAnnualTotal = useMemo(() => visibleAccounts.reduce(
    (sum, acc) => salaryAutoCompute && LOCKED_ACCOUNTS.has(acc)
      ? sum + storedLockedTotal(acc, annualBudgetData)
      : sum + (annualBudgetData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [visibleAccounts, annualBudgetData, storedLockedTotal, salaryAutoCompute]);

  // Current dimension total — uses blended actuals+computed for locked accounts
  const filteredDimTotal = useMemo(() => visibleAccounts.reduce(
    (sum, acc) => salaryAutoCompute && LOCKED_ACCOUNTS.has(acc)
      ? sum + lockedAnnualTotal(acc)
      : sum + (budgetData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [visibleAccounts, budgetData, lockedAnnualTotal, salaryAutoCompute]);

  // Unfiltered total using blended values for locked accounts (used in submit validation & modal)
  const dimTotal = useMemo(() => allAccounts.reduce(
    (sum, acc) => salaryAutoCompute && LOCKED_ACCOUNTS.has(acc)
      ? sum + lockedAnnualTotal(acc)
      : sum + (budgetData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [allAccounts, budgetData, lockedAnnualTotal, salaryAutoCompute]);

  // Reference total — uses STORED values for locked accounts (matches admin Forecast Status)
  const filteredRefTotal = useMemo(() => visibleAccounts.reduce(
    (sum, acc) => salaryAutoCompute && LOCKED_ACCOUNTS.has(acc)
      ? sum + storedLockedTotal(acc, referenceData)
      : sum + (referenceData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [visibleAccounts, referenceData, storedLockedTotal, salaryAutoCompute]);

  // Unfiltered reference total (used in submit validation) — stored values
  const refTotal = useMemo(() => allAccounts.reduce(
    (sum, acc) => salaryAutoCompute && LOCKED_ACCOUNTS.has(acc)
      ? sum + storedLockedTotal(acc, referenceData)
      : sum + (referenceData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [allAccounts, referenceData, storedLockedTotal, salaryAutoCompute]);

  const filteredVarianceTotal = filteredRefTotal - filteredDimTotal;

  // Actuals total scoped to visible accounts (category-filter aware)
  const filteredActualsTotal = useMemo(() => visibleAccounts.reduce(
    (sum, acc) => sum + (actualsFullData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [visibleAccounts, actualsFullData]);

  // Budget Consumption = Filtered Actuals / Filtered Dimension total (respects category filter)
  const budgetConsumptionPct = filteredDimTotal > 0
    ? (filteredActualsTotal / filteredDimTotal) * 100
    : 0;

  // ── CC-level (unfiltered) KPI values — always show full cost center totals ──
  const ccVarianceTotal = refTotal - dimTotal;
  const ccAnnualTotal = useMemo(() => allAccounts.reduce(
    (sum, acc) => salaryAutoCompute && LOCKED_ACCOUNTS.has(acc)
      ? sum + storedLockedTotal(acc, annualBudgetData)
      : sum + (annualBudgetData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [allAccounts, annualBudgetData, storedLockedTotal, salaryAutoCompute]);
  const ccActualsTotal = useMemo(() => allAccounts.reduce(
    (sum, acc) => sum + (actualsFullData[acc] || []).reduce((s, e) => s + (e.budget || 0), 0), 0
  ), [allAccounts, actualsFullData]);
  const ccBudgetConsumptionPct = dimTotal > 0 ? (ccActualsTotal / dimTotal) * 100 : 0;

  const kpiDimTotal = dimTotal;
  const kpiRefTotal = refTotal;


  const openExportModal = () => {
    setExportModalOpen(true);
    setExportFile(null);
    setExportPassword("");
    setExportNeedsPassword(false);
    setExportDimension("Annual Budget");
    setExportValidation(null);
    setExportError(null);
    setExporting(false);
  };

  const handleExportValidate = async (file: File, pw?: string) => {
    setExportFile(file);
    setExportValidating(true);
    setExportValidation(null);
    setExportError(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("cost_center", costCenterNumber);
      if (pw || exportPassword) form.append("password", pw || exportPassword);
      const res = await api.post("/export/validate-template", form);
      setExportValidation(res.data);
      setExportPlanSheet(res.data.sheets?.planSheetName || "");
      setExportNeedsPassword(false);
    } catch (err: any) {
      const respData = err?.response?.data;
      const status = err?.response?.status;
      if (respData?.needsPassword || status === 422) {
        setExportNeedsPassword(true);
        setExportError(null);
      } else if (!err?.response) {
        setExportError("Cannot reach server. Please check the backend is running.");
      } else {
        setExportError(respData?.error || `Validation failed (${status})`);
      }
    } finally {
      setExportValidating(false);
    }
  };

  const handleExportPasswordSubmit = () => {
    if (!exportFile || !exportPassword) return;
    handleExportValidate(exportFile, exportPassword);
  };

  const handleExportDownload = async () => {
    if (!exportFile) return;
    setExporting(true);
    setExportError(null);
    try {
      const form = new FormData();
      form.append("file", exportFile);
      form.append("cost_center", costCenterNumber);
      if (exportPassword) form.append("password", exportPassword);
      form.append("dimension", exportDimension);
      if (exportPlanSheet) form.append("planSheetOverride", exportPlanSheet);
      const res = await api.post("/export/populate-template", form, { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `${costCenterNumber}_export.xlsx`;
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      setExportModalOpen(false);

      const catRows = Object.entries(subtotals)
        .sort(([a], [b]) => getCategoryRank(a) - getCategoryRank(b))
        .map(([cat, total]) => ({ cat, label: CATEGORY_DISPLAY[cat] ?? cat, total }))
        .filter(r => r.total !== 0);
      setDownloadSummary({ filename, dimension: exportDimension, categoryTotals: catRows, grandTotal: filteredDimTotal });
    } catch (err: any) {
      setExportError(err?.response?.data?.error || "Export failed");
    } finally {
      setExporting(false);
    }
  };


  // CC-wide budget consumption: all actuals vs target (reference + variance allowance)
  const ccTargetTotal = overallAnnualBudgetTotal + varianceAllowed;
  const ccConsumptionPct = ccTargetTotal > 0 ? (overallActualsTotal / ccTargetTotal) * 100 : 0;
  const consumptionColor = ccConsumptionPct > 100 ? "#dc2626" : "#16a34a";

  // Per-category reference totals for variance % in pills (memoized)
  const refSubtotals = useMemo(() => {
    const totals: Record<string, number> = {};
    Object.values(referenceData).forEach((entries) => {
      entries.forEach(({ category, budget }) => {
        const key = category?.trim().toUpperCase() || "OTHERADJUSTMENTS";
        totals[key] = (totals[key] || 0) + budget;
      });
    });
    return totals;
  }, [referenceData]);

  // Footer monthly totals (memoized — avoids re-reducing on every render)
  const footerMonthTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    months.forEach(m => {
      totals[m] = visibleAccounts.reduce((sum, acc) => {
        const lk = salaryAutoCompute ? getLockedKey(acc) : null;
        if (lk) return sum + lockedMonthValue(lk, m);
        const e = (budgetData[acc] || []).find(x => x.month === m);
        return sum + (e?.budget || 0);
      }, 0);
    });
    return totals;
  }, [visibleAccounts, budgetData, lockedMonthValue, salaryAutoCompute]);

  // Footer variance monthly totals (memoized)
  const footerVarTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    months.forEach(m => {
      totals[m] = visibleAccounts.reduce((sum, acc) => {
        const ref = (referenceData[acc] || []).find(x => x.month === m);
        const dim = (budgetData[acc] || []).find(x => x.month === m);
        return sum + ((ref?.budget || 0) - (dim?.budget || 0));
      }, 0);
    });
    return totals;
  }, [visibleAccounts, referenceData, budgetData]);

  const refDim = dimensionReference[dimension] ?? "Annual Budget";
  const refLabel = refDim === "Annual Budget" ? "Annual"
    : refDim === "Actuals" ? "Actuals"
    : refDim.replace(" Forecast", "");
  return (
    <div className="budget-container">

      {/* ── KPI Section ──────────────────────────────────────────────────── */}
      <div className="kpi-section">
      <div className="kpi-strip">
        {/* Left group: totals */}
        <div className="kpi-strip-left">
          {dimension !== "Annual Budget" ? (
            <div className="kpi-card kpi-card--neutral">
              <span className="kpi-label">
                {`${dimension.replace(" Forecast", "")} Target`}
              </span>
              <span className="kpi-value">{formatCurrency(kpiRefTotal + varianceAllowed)}</span>
              <span className="kpi-target-breakdown">
                {refDim}{varianceAllowed !== 0 ? ` ${varianceAllowed > 0 ? "+" : "−"} ${formatCurrency(Math.abs(varianceAllowed))} variance` : ""}
              </span>
            </div>
          ) : (
            <div className="kpi-card kpi-card--neutral">
              <span className="kpi-label">Annual Budget</span>
              <span className="kpi-value">{formatCurrency(ccAnnualTotal)}</span>
            </div>
          )}
          {dimension !== "Annual Budget" && (<>
            <div className="kpi-card kpi-card--current">
              <span className="kpi-current-header">
                <span className="kpi-current-name">{dimension}</span>
                <span className="kpi-current-tag">Current</span>
              </span>
              <span className="kpi-value kpi-value--current">{formatCurrency(kpiDimTotal)}</span>
              {submissionStatus && (
                <span className={`kpi-status-badge kpi-status-badge--${submissionStatus}`}>
                  {submissionStatus === "submitted" ? "Submitted" : "Draft"}
                </span>
              )}
            </div>
          </>)}
        </div>

        {/* Right group: variance, growth, consumption (forecast dims only) */}
        {dimension !== "Annual Budget" && (() => {
          const refLabel2   = `${dimension} Target`;
          const targetTotal = kpiRefTotal + varianceAllowed;
          const varDollar   = Math.round(kpiDimTotal - targetTotal);
          const growthPct   = targetTotal > 0
            ? (varDollar / targetTotal) * 100
            : 0;
          const isOver = varDollar > 0;
          return (
            <div className="kpi-strip-right">
              <div className={`budget-header-kpi ${isOver ? "budget-header-kpi--bad" : "budget-header-kpi--good"}`}>
                <span className="budget-header-kpi-label">$ Variance vs {refLabel2}</span>
                <span className="budget-header-kpi-value">
                  {isOver ? `(${formatCurrency(Math.abs(varDollar))})` : formatCurrency(Math.abs(varDollar))}
                </span>
                <span className={`kpi-variance-icon ${isOver ? "kpi-variance-icon--warn" : "kpi-variance-icon--ok"}`}>
                  <span className="material-icons">{isOver ? "error_outline" : "check_circle"}</span>
                  {isOver ? "Over Target" : "Meets Target"}
                </span>
              </div>
              <div className={`budget-header-kpi ${isOver ? "budget-header-kpi--bad" : "budget-header-kpi--good"}`}>
                <span className="budget-header-kpi-label">% Change vs {refLabel2}</span>
                <span className="budget-header-kpi-value">
                  {isOver ? `(${Math.abs(growthPct).toFixed(1)}%)` : `${Math.abs(growthPct).toFixed(1)}%`}
                </span>
              </div>
              <div className={`budget-header-kpi ${ccConsumptionPct > 100 ? "budget-header-kpi--bad" : "budget-header-kpi--good"}`}>
                <span className="budget-header-kpi-label">YTD Budget Used</span>
                <span className="budget-header-kpi-value" style={{ color: consumptionColor }}>
                  {ccConsumptionPct.toFixed(1)}%
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ── Category Subtotals Pill Strip ─────────────────────────────────── */}
      {Object.keys(subtotals).length > 0 && (
          <div className="subtotals-strip">
            {Object.entries(subtotals)
              .filter(([cat]) => CATEGORY_ORDER[cat] !== undefined)
              .sort(([a], [b]) => getCategoryRank(a) - getCategoryRank(b))
              .map(([cat, val]) => {
                const refVal = refSubtotals[cat] || 0;
                const catVariance = refVal - val;
                const catVarPct = refVal !== 0 ? Math.abs((catVariance / refVal) * 100) : 0;
                const catColor = catVariance >= 0 ? "#15803d" : "#dc2626";
                const pillColor = CATEGORY_COLORS[cat] || "#64748b";
                return (
                  <div key={cat} className="subtotal-pill" style={{ background: pillColor + "12", borderColor: pillColor + "30" }}>
                    <span className="subtotal-cat" style={{ color: pillColor }}>{CATEGORY_DISPLAY[cat] ?? cat}</span>
                    <span className="subtotal-val">{formatCurrency(val)}</span>
                    {refVal !== 0 && (
                      <span className="subtotal-var" style={{ color: catColor }}>
                        {catVariance >= 0 ? "+" : ""}{catVarPct.toFixed(1)}%
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
      )}
      </div>{/* end kpi-section */}

      {/* ── Toolbar ───────────────────────────────────────────────────────── */}
      <div className="filters">
        <div className="filter-group">
          <label className="filter-label">
            Category
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="All">All</option>
              {uniqueCategories.map(cat => (
                <option key={cat} value={cat}>{CATEGORY_DISPLAY[cat] ?? cat}</option>
              ))}
            </select>
          </label>
        </div>
        <div className="filter-separator" />
        <div className="filter-group">
          <label className="filter-label">
            Search Account
            <input
              type="text"
              value={accountSearch}
              onChange={e => setAccountSearch(e.target.value)}
              placeholder="Account name…"
              className="account-search-input"
            />
          </label>
        </div>

        {/* ── Dimension Segment Switcher (portalled into app-sub-nav) ── */}
        {(() => {
          const slot = document.getElementById("forecast-cycle-slot");
          if (!slot) return null;
          return createPortal(
            <div className="dim-switcher-portal">
              <span className="dim-switcher-label">Forecast Cycle</span>
              <div className="dim-switcher">
                {[
                  { dim: "Annual Budget",    label: "Annual",  color: "#475569" },
                  { dim: "July Forecast",    label: "July",    color: "#4A90E2" },
                  { dim: "October Forecast", label: "October", color: "#ea580c" },
                  { dim: "January Forecast", label: "January", color: "#059669" },
                ].map(({ dim, label, color }) => {
                  const isActive = dimension === dim;
                  const open = isDimOpen(dim);
                  return (
                    <button
                      key={dim}
                      className={`dim-pill${isActive ? " dim-pill--active" : ""}${!open ? " dim-pill--disabled" : ""}`}
                      style={isActive
                        ? { background: color, boxShadow: `0 2px 8px ${color}44` }
                        : { color: open ? "#374151" : "#9ca3af" }}
                      onClick={() => open && setDimension(dim)}
                      title={open ? dim : dimWindowTitle(dim)}
                      disabled={!open}
                    >
                      {label}
                      {forecastDim === dim && <span className={`dim-pill-dot${forecastActive ? " dim-pill-dot--live" : ""}`} />}
                    </button>
                  );
                })}
              </div>
            </div>,
            slot
          );
        })()}

        <div className="filter-separator" />
        <button
          onClick={() => setExcelMode(v => !v)}
          className={`excel-mode-btn${excelMode ? " excel-mode-btn--active" : ""}`}
          title={excelMode ? "Switch back to default view with variance rows" : "Removes variance rows to tighten the view, like a spreadsheet. Use Tab to move between cells."}
        >
          <span className="material-icons">table_chart</span>
          {excelMode ? "Default View" : "Excel View"}
          {Date.now() < new Date("2026-10-29").getTime() && <span style={{ position: "absolute", top: -7, right: -10, background: "oklch(0.55 0.22 30)", color: "#fff", fontSize: 8, fontWeight: 800, padding: "2px 5px", borderRadius: 4, letterSpacing: "0.05em", lineHeight: 1, textTransform: "uppercase" }}>New</span>}
        </button>
        <button
          onClick={() => setSimplifiedView(v => !v)}
          className={`excel-mode-btn${simplifiedView ? " excel-mode-btn--active" : ""}`}
          title={simplifiedView ? "Show all accounts including $0 lines" : "Hides accounts with $0 budget and no variance to reduce clutter"}
          style={{ position: "relative" }}
        >
          <span className="material-icons">{simplifiedView ? "visibility_off" : "visibility"}</span>
          {simplifiedView ? "Show All" : "Simplified View"}
          {Date.now() < new Date("2026-10-29").getTime() && <span style={{ position: "absolute", top: -7, right: -10, background: "oklch(0.55 0.22 30)", color: "#fff", fontSize: 8, fontWeight: 800, padding: "2px 5px", borderRadius: 4, letterSpacing: "0.05em", lineHeight: 1, textTransform: "uppercase" }}>New</span>}
        </button>

        <div style={{ flex: 1, display: "flex", justifyContent: "center" }}>
          <button
            onClick={() => { setFcCommentDim(dimension); setFcCommentDraft(fcComments[dimension] || ""); setFcCommentOpen(true); }}
            title="Track changes and notes across forecast dimensions"
            style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 16px", fontSize: 13, fontWeight: 700, border: "1.5px solid oklch(0.50 0.14 250)", background: "oklch(0.94 0.03 250)", color: "oklch(0.40 0.12 250)", borderRadius: 6, cursor: "pointer" }}
          >
            <span className="material-icons" style={{ fontSize: 17 }}>sticky_note_2</span>
            My Notes
            {Date.now() < new Date("2026-10-29").getTime() && <span style={{ position: "absolute", top: -7, right: -10, background: "oklch(0.55 0.22 30)", color: "#fff", fontSize: 8, fontWeight: 800, padding: "2px 5px", borderRadius: 4, letterSpacing: "0.05em", lineHeight: 1, textTransform: "uppercase" }}>New</span>}
          </button>
        </div>

        <div className="action-buttons">
          <button onClick={openExportModal} className="export-button" title={`Download ${dimension} · ${costCenterNumber} · ${fiscalYear || "Plan"}`}>
            <span className="material-icons">download</span>
            Download
          </button>
          <button onClick={handleSaveDraft} className="save-draft-button" disabled={updatedData.length === 0}>
            <span className="material-icons">save</span>
            Save{updatedData.length > 0 ? ` (${updatedData.length})` : ""}
          </button>
          {(() => {
            const effectiveTarget = ccTarget > 0 ? ccTarget : (kpiRefTotal + varianceAllowed);
            const overTarget = dimension !== "Annual Budget" && effectiveTarget > 0 && Math.round(kpiDimTotal) > Math.round(effectiveTarget);
            const hasChanges = updatedData.length > 0;
            const canSubmitDraft = submissionStatus === "draft" && !overTarget;
            const isDisabled = overTarget || (!hasChanges && !canSubmitDraft);
            return (
              <button
                onClick={handleSubmit}
                className="submit-button"
                disabled={isDisabled}
                title={overTarget
                  ? `Submit inactive until forecast is within ${ccTarget > 0 ? "target" : "budget"} (${formatCurrency(effectiveTarget)})`
                  : ""}
              >
                <span className="material-icons">send</span>
                Submit{hasChanges ? ` (${updatedData.length})` : ""}
              </button>
            );
          })()}
        </div>
      </div>


      {/* ── Floating Notes Panel ─────────────────────────────────────────── */}
      {fcCommentOpen && (() => {
        const NOTE_DIMS = ["Annual Budget", "July Forecast", "October Forecast", "January Forecast"];
        const activeFcDim = fcCommentDim || dimension;
        const switchFcDim = (d: string) => {
          if (d === activeFcDim) return;
          setFcComments(prev => ({ ...prev, [activeFcDim]: fcCommentDraft }));
          setFcCommentDim(d);
          setFcCommentDraft(fcComments[d] || "");
        };
        return (
        <>
          <div onClick={() => setFcCommentOpen(false)} style={{ position: "fixed", inset: 0, background: "oklch(0.15 0.02 250 / 0.20)", zIndex: 999 }} />
          <div style={{ position: "fixed", top: "8%", right: 24, width: "38vw", minWidth: 480, maxWidth: 700, height: "48vh", minHeight: 340, zIndex: 1000, background: "oklch(1 0 0)", borderRadius: 10, boxShadow: "0 24px 64px oklch(0.06 0.02 250 / 0.22)", display: "flex", flexDirection: "column", border: "1.5px solid oklch(0.9 0.01 250)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid oklch(0.92 0.01 250)" }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "oklch(0.12 0.02 250)", display: "flex", alignItems: "center", gap: 6 }}>
                <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.45 0.14 250)" }}>sticky_note_2</span> My Notes
              </span>
              <button onClick={() => setFcCommentOpen(false)} style={{ border: "none", background: "none", cursor: "pointer", padding: 4, borderRadius: 4 }}>
                <span className="material-icons" style={{ fontSize: 18, color: "oklch(0.5 0.02 250)" }}>close</span>
              </button>
            </div>
            <div style={{ display: "flex", gap: 6, padding: "10px 18px 0", flexWrap: "wrap" }}>
              {NOTE_DIMS.map(d => (
                <button key={d} onClick={() => switchFcDim(d)}
                  style={{ padding: "4px 12px", fontSize: 11, borderRadius: 20, border: d === activeFcDim ? "1px solid oklch(0.45 0.14 250)" : "1px solid oklch(0.92 0.01 250)", background: d === activeFcDim ? "oklch(0.45 0.14 250)" : "oklch(0.97 0.005 250)", color: d === activeFcDim ? "oklch(0.95 0.005 250)" : "oklch(0.5 0.02 250)", cursor: "pointer", fontWeight: 700, letterSpacing: "0.3px", transition: "all 0.15s ease" }}
                >{d.replace(" Forecast", " Fcst")}</button>
              ))}
            </div>
            <textarea
              autoFocus
              style={{ flex: 1, margin: "10px 18px", padding: "10px 12px", fontSize: 13, lineHeight: 1.6, border: "1px solid oklch(0.88 0.01 250)", borderRadius: 8, resize: "none", fontFamily: "inherit", color: "oklch(0.12 0.02 250)", background: "oklch(0.99 0.003 250)" }}
              value={fcCommentDraft}
              onChange={e => setFcCommentDraft(e.target.value)}
              placeholder={`Notes for ${activeFcDim}…`}
            />
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 8, padding: "0 18px 14px" }}>
              {fcCommentSaved && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, color: "oklch(0.42 0.15 155)", transition: "opacity 0.3s", marginRight: "auto" }}>
                  <span className="material-icons" style={{ fontSize: 16 }}>check_circle</span> Note saved
                </span>
              )}
              <button
                onClick={() => { setFcCommentOpen(false); setFcCommentDraft(fcComments[activeFcDim] || ""); }}
                style={{ padding: "7px 14px", fontSize: 13, fontWeight: 600, borderRadius: 7, border: "1px solid oklch(0.88 0.01 250)", background: "oklch(1 0 0)", color: "oklch(0.25 0.02 250)", cursor: "pointer", transition: "background 0.15s" }}
              >Cancel</button>
              <button
                onClick={() => {
                  setFcCommentSaving(true);
                  api.put("/settings/forecast-comment", { dimension: activeFcDim, cost_center: costCenterNumber, comment: fcCommentDraft })
                    .then(() => {
                      setFcComments(prev => ({ ...prev, [activeFcDim]: fcCommentDraft }));
                      setFcCommentSaved(true);
                      setTimeout(() => setFcCommentSaved(false), 2500);
                    })
                    .catch(() => {})
                    .finally(() => setFcCommentSaving(false));
                }}
                disabled={fcCommentSaving}
                style={{ padding: "7px 14px", fontSize: 13, fontWeight: 600, borderRadius: 7, border: "none", background: "oklch(0.45 0.14 250)", color: "oklch(0.95 0.005 250)", cursor: "pointer", transition: "background 0.15s" }}
              >{fcCommentSaving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </>
        );
      })()}

      {/* ── Budget Table ──────────────────────────────────────────────────── */}
      <div className="budget-table-wrapper">
        <table className="budget-table">
          <thead>
            <tr>
              <th
                ref={accountColRef}
                className="account-col"
                style={{ width: accountColWidth, minWidth: accountColWidth }}
              >
                Account
                <div className="resizer" onMouseDown={handleMouseDown} />
              </th>
              {!excelMode && <th className="row-type-col">Type</th>}
              {months.map((month) => <th key={month}>{monthAbbr[month]}</th>)}
              <th style={{ width: 82 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const showHeaders = categoryFilter === "All" && !accountSearch;
              const colCount = excelMode ? 14 : 15;
              let prevCat = "";
              return visibleAccounts.flatMap((account) => {
                const rows: React.ReactNode[] = [];

                const accountCat = getAccountCategory(account);
                const acctCatColor = CATEGORY_COLORS[accountCat] || "#64748b";

                if (showHeaders) {
                  if (accountCat !== prevCat) {
                    prevCat = accountCat;
                    rows.push(
                      <tr key={`cat-hdr-${accountCat}`} className="category-section-header" style={{ background: acctCatColor }}>
                        <td colSpan={colCount} style={{ background: acctCatColor }}>{CATEGORY_DISPLAY[accountCat] ?? accountCat}</td>
                      </tr>
                    );
                  }
                }

                // ── Locked auto-calc row (salary-derived) ──────────────────────
                const lockedKey = salaryAutoCompute ? getLockedKey(account) : null;
                if (lockedKey) {
                  const rowTotal = lockedAnnualTotal(lockedKey);
                  rows.push(
                    <tr key={account} className="locked-auto-row" title="Computed from Salary and BIP views. Edit those views to change these values.">
                      <td
                        className="account-col"
                        style={{ width: accountColWidth, minWidth: accountColWidth, borderLeft: `3px solid ${acctCatColor}`, background: acctCatColor + "08" }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}>
                          <span className="material-icons" style={{ fontSize: 12, color: "#94a3b8", flexShrink: 0 }}>lock</span>
                          <span style={{ fontWeight: 700, fontSize: 11 }}>{lockedKey}</span>
                        </div>
                        <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>{LOCKED_ACCOUNT_LABELS[lockedKey]}</div>
                      </td>
                      {!excelMode && (
                        <td className="row-type-col">
                          <span className="row-badge row-badge--auto">Computed</span>
                        </td>
                      )}
                      {months.map(m => {
                        const val = lockedMonthValue(lockedKey, m);
                        const isActual = actualsMonths.includes(m);
                        return (
                          <td key={`${account}-locked-${m}`} style={isActual ? { color: "#0369a1", fontWeight: 600 } : undefined}
                              title={isActual ? "Actual" : "Computed"}>
                            {val > 0 ? val.toLocaleString() : <span style={{ color: "#d1d5db" }}>–</span>}
                          </td>
                        );
                      })}
                      <td className="total-cell">
                        {rowTotal > 0 ? `$${rowTotal.toLocaleString()}` : "–"}
                      </td>
                    </tr>
                  );
                  return rows;
                }

                const entries = budgetData[account] || [];
                const refEntries = referenceData[account] || [];
                const totalBudget = entries.reduce((sum, e) => sum + (e.budget || 0), 0);
                const totalRef = refEntries.reduce((sum, e) => sum + (e.budget || 0), 0);
                const totalVariance = totalRef - totalBudget;

                const dimensionLabel = dimension === "Annual Budget"
                  ? "Annual"
                  : dimension === "Actuals"
                    ? "Actuals"
                    : dimension.replace(" Forecast", "");

                // ── Excel mode: single clean editable row, no variance ──────────
                if (excelMode) {
                  rows.push(
                    <tr key={account} className="forecast-row excel-mode-row">
                      <td
                        className="account-col"
                        style={{ width: accountColWidth, minWidth: accountColWidth, borderLeft: `3px solid ${acctCatColor}`, background: acctCatColor + "08" }}
                      >
                        <AccountCell account={account} />
                      </td>
                      {months.map((monthName) => {
                        const entry = entries.find((e) => e.month === monthName);
                        const editable = isDimOpen(dimension) && dimension === currentDimension && isEditableMonth(dimension, monthName);
                        return (
                          <td key={`${account}-${dimension}-${monthName}`} className={editable ? "" : "excel-cell-locked"}>
                            <input
                              type="number"
                              step="1"
                              className={`editable-cell${editable ? " editable-cell--active excel-cell" : ""}`}
                              value={Math.round(entry?.budget || 0)}
                              onChange={(e) => editable && handleChange(account, monthName, Math.round(Number(e.target.value)))}
                              onKeyDown={editable ? handleCellKeyDown : undefined}
                              disabled={!editable}
                              tabIndex={editable ? 0 : -1}
                            />
                          </td>
                        );
                      })}
                      <td className="total-cell">{formatCurrencyFull(totalBudget)}</td>
                    </tr>
                  );
                  return rows;
                }

                // ── Default mode: two rows (dimension + variance) + optional MPY row
                const mpyEntries = mpyReferenceData[account] || [];
                const hasMpy = mpyEntries.length > 0;
                const accountRowSpan = hasMpy ? 3 : 2;

                rows.push(
                  <React.Fragment key={account}>
                    {/* Row 1: Selected Dimension */}
                    <tr className="forecast-row">
                      <td
                        rowSpan={accountRowSpan}
                        className="account-col"
                        style={{ width: accountColWidth, minWidth: accountColWidth, borderLeft: `3px solid ${acctCatColor}`, background: acctCatColor + "08" }}
                      >
                        <AccountCell account={account} />
                      </td>
                      <td className="row-type-col">
                        <span className="row-badge row-badge--forecast">{dimensionLabel}</span>
                      </td>
                      {months.map((monthName) => {
                        const entry = entries.find((e) => e.month === monthName);
                        const editable = isDimOpen(dimension) && dimension === currentDimension && isEditableMonth(dimension, monthName);
                        return (
                          <td key={`${account}-${dimension}-${monthName}`}>
                            <input
                              type="number"
                              step="1"
                              className={`editable-cell${editable ? " editable-cell--active" : ""}`}
                              value={Math.round(entry?.budget || 0)}
                              onChange={(e) => handleChange(account, monthName, Math.round(Number(e.target.value)))}
                              onKeyDown={editable ? handleCellKeyDown : undefined}
                              disabled={!editable}
                            />
                          </td>
                        );
                      })}
                      <td className="total-cell">{formatCurrencyFull(totalBudget)}</td>
                    </tr>

                    {/* Row 2: Variance (ref − selected) */}
                    <tr className="variance-row">
                      <td className="row-type-col">
                        <span className="row-badge row-badge--variance">Variance vs {refLabel}</span>
                      </td>
                      {months.map((monthName) => {
                        const selectedEntry = entries.find((e) => e.month === monthName);
                        const refEntry = refEntries.find((e) => e.month === monthName);
                        const monthlyVariance = (refEntry?.budget || 0) - (selectedEntry?.budget || 0);
                        return (
                          <td
                            key={`${account}-Variance-${monthName}`}
                            className={monthlyVariance >= 0 ? "var-positive" : "var-negative"}
                          >
                            {formatVarianceNumber(monthlyVariance)}
                          </td>
                        );
                      })}
                      <td className={`total-cell ${totalVariance >= 0 ? "var-positive" : "var-negative"}`}>
                        {formatVarianceNumber(totalVariance)}
                      </td>
                    </tr>

                    {/* Row 3: FY26 MPY Reference (only when editing FY27+ Annual Budget) */}
                    {hasMpy && (
                      <tr className="variance-row mpy-reference-row">
                        <td className="row-type-col">
                          <span className="row-badge row-badge--mpy">FY26 MPY</span>
                        </td>
                        {months.map((monthName) => {
                          const mpyEntry = mpyEntries.find((e) => e.month === monthName);
                          return (
                            <td key={`${account}-mpy-${monthName}`} style={{ color: "oklch(0.45 0.04 250)", fontStyle: "italic" }}>
                              {(mpyEntry?.budget || 0) > 0 ? Math.round(mpyEntry!.budget).toLocaleString() : <span style={{ color: "#d1d5db" }}>–</span>}
                            </td>
                          );
                        })}
                        <td className="total-cell" style={{ color: "oklch(0.45 0.04 250)", fontStyle: "italic" }}>
                          {formatCurrencyFull(mpyEntries.reduce((s, e) => s + (e.budget || 0), 0))}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
                return rows;
              });
            })()}
          </tbody>
          <tfoot>
            {/* Footer Row 1: Dimension totals */}
            <tr className={`total-footer-row total-footer-dim${excelMode ? " total-footer-dim--sole" : ""}`}>
              <td
                className="account-col total-footer-label"
                style={{ width: accountColWidth, minWidth: accountColWidth }}
              >
                Total
              </td>
              {!excelMode && (
                <td className="row-type-col">
                  <span className="row-badge row-badge--forecast">
                    {dimension === "Annual Budget" ? "Annual" : dimension === "Actuals" ? "Actuals" : dimension.replace(" Forecast", "")}
                  </span>
                </td>
              )}
              {months.map((monthName) =>
                <td key={`ft-${monthName}`} className="total-cell total-footer-cell">{formatCurrencyFull(footerMonthTotals[monthName])}</td>
              )}
              <td className="total-cell total-footer-cell">{formatCurrencyFull(filteredDimTotal)}</td>
            </tr>

            {/* Footer Row 2: Variance totals (hidden in Excel mode) */}
            {!excelMode && (
              <tr className="total-footer-row total-footer-var">
                <td
                  className="account-col total-footer-label"
                  style={{ width: accountColWidth, minWidth: accountColWidth }}
                >
                  Variance vs {refLabel}
                </td>
                <td className="row-type-col">
                  <span className="row-badge row-badge--variance">Variance vs {refLabel}</span>
                </td>
                {months.map((monthName) => {
                  const monthVar = footerVarTotals[monthName];
                  return (
                    <td key={`fv-${monthName}`} className={`total-footer-cell ${monthVar >= 0 ? "var-positive" : "var-negative"}`}>
                      {formatVarianceNumber(monthVar)}
                    </td>
                  );
                })}
                <td className={`total-cell total-footer-cell ${filteredVarianceTotal >= 0 ? "var-positive" : "var-negative"}`}>
                  {formatVarianceNumber(filteredVarianceTotal)}
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      {/* ── Success Modal ────────────────────────────────────────────────── */}
      {successModal && (
        <div className="var-alert-overlay" onClick={() => setSuccessModal(null)}>
          <div className="var-alert-modal success-modal" onClick={e => e.stopPropagation()}>
            <div className="var-alert-icon success-modal-icon">
              <span className="material-icons">check_circle</span>
            </div>
            <h3 className="var-alert-title">Budget Submitted</h3>
            <p className="var-alert-dim" style={{ marginBottom: 0 }}>Summary</p>
            <div className="var-alert-numbers">
              <div className="var-alert-row">
                <span>{successModal.dim} Total</span>
                <span className="var-alert-val">{formatCurrency(successModal.total)}</span>
              </div>
              <div className={`var-alert-row var-alert-row--total ${successModal.variance >= 0 ? "success-modal-row--fav" : "success-modal-row--unfav"}`}>
                <span>Variance vs {successModal.dim} Target</span>
                <span className={`var-alert-val ${successModal.variance >= 0 ? "success-modal-val--fav" : "var-alert-val--bad"}`}>
                  {successModal.variance < 0 ? `(${formatCurrency(Math.abs(successModal.variance))})` : formatCurrency(successModal.variance)}
                </span>
              </div>
            </div>
            {successModal.closeDate && (
              <p className="success-modal-deadline">
                <span className="material-icons" style={{ fontSize: 15, verticalAlign: "middle", marginRight: 4 }}>event</span>
                Submissions open until <strong>{successModal.closeDate}</strong>
              </p>
            )}
            <button className="var-alert-btn success-modal-btn" onClick={() => setSuccessModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Draft Saved Modal ──────────────────────────────────────────── */}
      {draftSavedModal && (
        <div className="var-alert-overlay" onClick={() => setDraftSavedModal(null)}>
          <div className="var-alert-modal draft-saved-modal" onClick={e => e.stopPropagation()}>
            <div className="var-alert-icon draft-saved-modal-icon">
              <span className="material-icons">bookmark_added</span>
            </div>
            <h3 className="var-alert-title">Draft Saved</h3>
            <p className="draft-saved-detail">
              {draftSavedModal.count} value{draftSavedModal.count > 1 ? "s" : ""} saved to <strong>{draftSavedModal.dim}</strong>
            </p>
            <p className="draft-saved-note">
              Your changes have been saved but not submitted. Submit when you are ready for final review.
            </p>
            <button className="var-alert-btn draft-saved-btn" onClick={() => setDraftSavedModal(null)}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Download Summary Modal ────────────────────────────────────────── */}
      {downloadSummary && (
        <div className="dl-summary-overlay" onClick={() => setDownloadSummary(null)}>
          <div className="dl-summary-modal" onClick={e => e.stopPropagation()}>
            <div className="dl-summary-header">
              <span className="material-icons dl-summary-icon">check_circle</span>
              <span>Download Complete</span>
              <button className="dl-summary-close" onClick={() => setDownloadSummary(null)}>
                <span className="material-icons">close</span>
              </button>
            </div>

            <div className="dl-summary-meta">
              <div className="dl-summary-row">
                <span className="dl-meta-label">File</span>
                <span className="dl-meta-value dl-filename">{downloadSummary.filename}</span>
              </div>
              <div className="dl-summary-row">
                <span className="dl-meta-label">Cost Center</span>
                <span className="dl-meta-value">{costCenterNumber}{costCenterName ? ` – ${costCenterName}` : ""}</span>
              </div>
              {costCenterOwner && (
                <div className="dl-summary-row">
                  <span className="dl-meta-label">Owner</span>
                  <span className="dl-meta-value">{costCenterOwner}</span>
                </div>
              )}
              <div className="dl-summary-row">
                <span className="dl-meta-label">Dimension</span>
                <span className="dl-meta-value">{downloadSummary.dimension}</span>
              </div>
            </div>

            <table className="dl-summary-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th className="dl-col-num">Total</th>
                </tr>
              </thead>
              <tbody>
                {downloadSummary.categoryTotals.map(r => (
                  <tr key={r.cat}>
                    <td>{r.label}</td>
                    <td className="dl-col-num">{formatCurrency(r.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="dl-summary-total-row">
                  <td>Total Forecast</td>
                  <td className="dl-col-num">{formatCurrency(downloadSummary.grandTotal)}</td>
                </tr>
              </tfoot>
            </table>

            <div className="dl-summary-footer">
              <button className="dl-summary-btn" onClick={() => setDownloadSummary(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showVarAlert && (
        <div className="var-alert-overlay" onClick={() => setShowVarAlert(false)}>
          <div className="var-alert-modal" onClick={e => e.stopPropagation()}>
            <div className="var-alert-icon">
              <span className="material-icons">warning</span>
            </div>
            <h3 className="var-alert-title">Cannot Submit: Budget Exceeded</h3>
            <p className="var-alert-dim">Total Variance for <strong>{dimension}</strong></p>
            <div className="var-alert-numbers">
              <div className="var-alert-row">
                <span>{dimension.replace(" Forecast", "")} Forecast</span>
                <span className="var-alert-val var-alert-val--bad">{formatCurrency(kpiDimTotal)}</span>
              </div>
              <div className="var-alert-row">
                <span>{refDim}</span>
                <span className="var-alert-val">{formatCurrency(kpiRefTotal)}</span>
              </div>
              {varianceAllowed > 0 && (
                <div className="var-alert-row">
                  <span>Variance allowed</span>
                  <span className="var-alert-val">{formatCurrency(varianceAllowed)}</span>
                </div>
              )}
              <div className="var-alert-row var-alert-row--total">
                <span>Over target by</span>
                <span className="var-alert-val var-alert-val--bad">{formatCurrency(kpiDimTotal - kpiRefTotal - varianceAllowed)}</span>
              </div>
            </div>
            <p className="var-alert-msg">
              Reduce your forecast to within the allowed range, then resubmit.<br />
              {varianceAllowed > 0
                ? `Your total cannot exceed ${refDim} plus the ${formatCurrency(varianceAllowed)} variance allowance.`
                : `Your total cannot exceed ${refDim}. Contact your Finance Admin to request a variance allowance.`}
            </p>
            <button className="var-alert-btn" onClick={() => setShowVarAlert(false)}>
              Understood
            </button>
          </div>
        </div>
      )}

      {/* ── Export Template Modal ────────────────────────────────────────── */}
      {exportModalOpen && (
        <div className="export-modal-overlay" onClick={() => !exporting && setExportModalOpen(false)}>
          <div className="export-modal" onClick={e => e.stopPropagation()}>
            <div className="export-modal-header">
              <span className="material-icons">file_download</span>
              <span>Export {fiscalYear || "Plan"}: {costCenterNumber}</span>
              <button className="export-modal-close" onClick={() => setExportModalOpen(false)} disabled={exporting}>
                <span className="material-icons">close</span>
              </button>
            </div>

            {/* Step 1: File Upload */}
            {!exportValidation && !exportNeedsPassword && (
              <div className="export-modal-body">
                <p className="export-modal-desc">
                  Upload your budget template file. The system will write the current forecast data into the Plan, Salary, and BIP tabs, preserving your formulas and automations.
                </p>
                <label
                  className={`export-drop-zone ${exportValidating ? "export-drop-zone--loading" : ""}`}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add("export-drop-zone--hover"); }}
                  onDragLeave={e => e.currentTarget.classList.remove("export-drop-zone--hover")}
                  onDrop={e => { e.preventDefault(); e.currentTarget.classList.remove("export-drop-zone--hover"); const f = e.dataTransfer.files[0]; if (f) handleExportValidate(f); }}
                >
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    style={{ display: "none" }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleExportValidate(f); }}
                  />
                  <span className="material-icons export-drop-icon">upload_file</span>
                  <span className="export-drop-text">
                    {exportValidating ? "Validating..." : "Drop budget file here or click to browse"}
                  </span>
                  <span className="export-drop-hint">.xlsx budget template</span>
                </label>
                {exportError && <div className="export-error">{exportError}</div>}
              </div>
            )}

            {/* Step 1b: Password Required */}
            {!exportValidation && exportNeedsPassword && (
              <div className="export-modal-body">
                <p className="export-modal-desc">
                  This file is password-protected. Enter the password to continue.
                </p>
                <div className="export-pw-row">
                  <span className="material-icons export-pw-icon">lock</span>
                  <input
                    type="password"
                    className="export-pw-input"
                    placeholder="File password"
                    value={exportPassword}
                    onChange={e => setExportPassword(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") handleExportPasswordSubmit(); }}
                    autoFocus
                  />
                </div>
                {exportFile && (
                  <div className="export-pw-file">
                    <span className="material-icons" style={{ fontSize: 14 }}>description</span>
                    {exportFile.name}
                  </div>
                )}
                {exportError && <div className="export-error">{exportError}</div>}
                <div className="export-modal-actions">
                  <button
                    className="export-btn-secondary"
                    onClick={() => { setExportNeedsPassword(false); setExportFile(null); setExportPassword(""); setExportError(null); }}
                  >
                    Change File
                  </button>
                  <button
                    className="export-btn-primary"
                    onClick={handleExportPasswordSubmit}
                    disabled={!exportPassword || exportValidating}
                  >
                    {exportValidating ? "Unlocking..." : "Unlock & Continue"}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Verification + Dimension Selector */}
            {exportValidation && (
              <div className="export-modal-body">
                {!exportValidation.ccMatch && (
                  <div className="export-error" style={{ marginBottom: 14 }}>
                    <strong>Cost center mismatch:</strong> This file belongs to CC {exportValidation.fileCC}, but you are exporting CC {costCenterNumber}. Please upload the correct file.
                  </div>
                )}
                <p className="export-modal-desc">
                  Select the dimension to export, verify the tabs below, then click Export.
                </p>

                <div className="export-dim-selector">
                  {["Annual Budget", "July Forecast", "October Forecast", "January Forecast"].map(dim => (
                    <button
                      key={dim}
                      className={`export-dim-btn ${exportDimension === dim ? "export-dim-btn--active" : ""}`}
                      onClick={() => setExportDimension(dim)}
                      disabled={exporting}
                    >
                      {dim}
                    </button>
                  ))}
                </div>

                <div className="export-verify-heading">Tabs to update:</div>
                <div className="export-verify-grid">
                  <div className={`export-verify-card ${exportValidation.sheets.salary ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportValidation.sheets.salary ? "check_circle" : "cancel"}</span>
                    <span className="export-verify-label">Salary60010</span>
                    <span className="export-verify-detail">{exportValidation.sheets.salary ? `${exportValidation.counts.employees} employees` : "Not found"}</span>
                  </div>
                  <div className={`export-verify-card ${exportValidation.sheets.bip ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportValidation.sheets.bip ? "check_circle" : "cancel"}</span>
                    <span className="export-verify-label">BIP60022</span>
                    <span className="export-verify-detail">{exportValidation.sheets.bip ? `${exportValidation.counts.employees} employees` : "Not found"}</span>
                  </div>
                  <div className={`export-verify-card ${exportPlanSheet ? "export-verify-card--ok" : "export-verify-card--miss"}`}>
                    <span className="material-icons">{exportPlanSheet ? "check_circle" : "cancel"}</span>
                    {exportValidation.sheets.planSheets && exportValidation.sheets.planSheets.length > 1 ? (
                      <select
                        className="export-plan-select"
                        value={exportPlanSheet}
                        onChange={e => setExportPlanSheet(e.target.value)}
                      >
                        {exportValidation.sheets.planSheets.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="export-verify-label">{exportPlanSheet || "PLANFY__ (missing)"}</span>
                    )}
                    <span className="export-verify-detail">{exportPlanSheet ? `${exportValidation.counts.budgetAccounts} accounts` : "No plan sheet found"}</span>
                  </div>
                </div>

                <div className="export-meta">
                  <div><strong>Dimension:</strong> {exportDimension} {exportValidation.fy}</div>
                  <div><strong>Output file:</strong> {exportFile?.name?.replace(/\.[^.]+$/, "")}_{({"Annual Budget":"ab","July Forecast":"jff","October Forecast":"off","January Forecast":"janff"} as Record<string,string>)[exportDimension] || "ab"}_{new Date().toISOString().slice(0,10).replace(/-/g,"")}.xlsx</div>
                  {exportFile && <div><strong>Template:</strong> {exportFile.name}</div>}
                </div>

                {!exportValidation.valid && (
                  <div className="export-error">
                    Template is missing expected sheets. Found: {exportValidation.allSheets.join(", ")}
                  </div>
                )}
                {exportError && <div className="export-error">{exportError}</div>}

                <div className="export-modal-actions">
                  <button
                    className="export-btn-secondary"
                    onClick={() => { setExportValidation(null); setExportFile(null); setExportPassword(""); setExportNeedsPassword(false); setExportError(null); }}
                    disabled={exporting}
                  >
                    Change File
                  </button>
                  <button
                    className="export-btn-primary"
                    onClick={handleExportDownload}
                    disabled={exporting || !exportValidation.valid || !exportValidation.ccMatch}
                  >
                    {exporting ? "Exporting..." : "Export & Download"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {showTargetAlert && (
        <div className="var-alert-overlay" onClick={() => setShowTargetAlert(false)}>
          <div className="var-alert-modal" onClick={e => e.stopPropagation()}>
            <div className="var-alert-icon">
              <span className="material-icons">block</span>
            </div>
            <h3 className="var-alert-title">Cannot Submit: Exceeds Target</h3>
            <p className="var-alert-dim">Target set by Finance Admin for <strong>{dimension}</strong></p>
            <div className="var-alert-numbers">
              <div className="var-alert-row">
                <span>Your Forecast Total</span>
                <span className="var-alert-val var-alert-val--bad">{formatCurrency(kpiDimTotal)}</span>
              </div>
              <div className="var-alert-row">
                <span>Finance Target</span>
                <span className="var-alert-val">{formatCurrency(ccTarget)}</span>
              </div>
              <div className="var-alert-row var-alert-row--total">
                <span>Over target by</span>
                <span className="var-alert-val var-alert-val--bad">{formatCurrency(kpiDimTotal - ccTarget)}</span>
              </div>
            </div>
            <p className="var-alert-msg">
              Your forecast total exceeds the target set by Finance.<br />
              Reduce your forecast by {formatCurrency(kpiDimTotal - ccTarget)} and resubmit.
            </p>
            <button className="var-alert-btn" onClick={() => setShowTargetAlert(false)}>
              Understood
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetEditor;
